import { DeleteOutlined, EditOutlined, KeyOutlined, LockOutlined, SafetyCertificateOutlined, TeamOutlined, UnlockOutlined } from "@ant-design/icons";
import { Alert, App, Button, Checkbox, Form, Input, Modal, Select, Space, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { deleteResource, listResource } from "../../api/resourceApi";
import { assignUserOrganization, assignUserPermissions, clearUserPermissions, getUserOrganizations, getUserPermissions, listRoles, resetUserPassword, toggleUserLock, updateUserRoles } from "../../api/userAdminApi";
import { confirmDeletePreview } from "../../shared/components/ConfirmDeletePreview";
import { PersistedTable } from "../../shared/components/PersistedTable";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { useEntityFormLauncher } from "../../shared/entityForms/EntityFormLauncherHost";
import { getApiErrorMessage } from "../../shared/utils/apiError";

type UserRow = {
  id: string;
  fullName: string;
  email: string;
  roles: string[];
  isLocked: boolean;
};

const permissionGroups: Record<string, string[]> = {
  DailyEntry: ["DailyEntry.Read", "DailyEntry.Create", "DailyEntry.Update", "DailyEntry.Delete"],
  CashEntry: ["CashEntry.Read", "CashEntry.Create", "CashEntry.Update", "CashEntry.Delete"],
  BillEntry: ["BillEntry.Read", "BillEntry.Create", "BillEntry.Update", "BillEntry.Delete"],
  StatementEntry: ["StatementEntry.Read", "StatementEntry.Create", "StatementEntry.Update", "StatementEntry.Delete"],
  Reports: ["Reports.Read", "Reports.Create"],
  Quote: ["Quote.Read", "Quote.Create", "Quote.Update", "Quote.Delete"]
};

const permissionCascade = ["Read", "Create", "Update", "Delete"];

function isFullAccessRole(user?: UserRow | null) {
  return Boolean(user?.roles?.some((role) => ["Admin", "Accountant"].includes(role)));
}

export function UsersPage() {
  const { message } = App.useApp();
  const openForm = useEntityFormLauncher();
  const [rolesUser, setRolesUser] = useState<UserRow | null>(null);
  const [permissionsUser, setPermissionsUser] = useState<UserRow | null>(null);
  const [passwordUser, setPasswordUser] = useState<UserRow | null>(null);
  const [orgUser, setOrgUser] = useState<UserRow | null>(null);

  const query = useQuery({ queryKey: ["users"], queryFn: () => listResource<UserRow>("/users") });
  const roles = useQuery({ queryKey: ["roles"], queryFn: listRoles });

  const columns: ColumnsType<UserRow> = [
    { title: "#", width: 54, render: (_value, _row, index) => index + 1 },
    { title: "Name", dataIndex: "fullName" },
    { title: "Email", dataIndex: "email" },
    { title: "Roles", dataIndex: "roles", render: (items: string[] = []) => items.map((x) => <Tag key={x}>{x}</Tag>) },
    { title: "Locked", dataIndex: "isLocked", width: 110, render: (v) => <Tag color={v ? "red" : "green"}>{v ? "Yes" : "No"}</Tag> },
    {
      title: "",
      width: 250,
      fixed: "right",
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openForm({ module: "user", mode: "edit", id: row.id, source: "users", onSuccess: () => query.refetch() })} />
          <Button size="small" icon={<TeamOutlined />} onClick={() => setRolesUser(row)} />
          <Tooltip title={isFullAccessRole(row) ? "Admin / Accountant users already have full access" : "Permissions"}>
            <Button size="small" icon={<SafetyCertificateOutlined />} disabled={isFullAccessRole(row)} onClick={() => setPermissionsUser(row)} />
          </Tooltip>
          <Button size="small" icon={<KeyOutlined />} onClick={() => setPasswordUser(row)} />
          <Button size="small" icon={row.isLocked ? <UnlockOutlined /> : <LockOutlined />} onClick={async () => {
            try {
              await toggleUserLock(row.id);
              message.success("Lock state updated");
              query.refetch();
            } catch (err) {
              message.error(getApiErrorMessage(err, "Unable to update lock state"));
            }
          }} />
          <Button size="small" onClick={() => setOrgUser(row)}>Org</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDeletePreview({
            entityName: "User",
            fields: [
              { label: "Name", value: row.fullName },
              { label: "Email", value: row.email },
              { label: "Roles", value: row.roles?.join(", ") }
            ],
            onConfirm: async () => {
              try {
                await deleteResource("/users", row.id);
                message.success("User deleted");
                query.refetch();
              } catch (err) {
                message.error(getApiErrorMessage(err, "Unable to delete user"));
              }
            }
          })} />
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeaderActions
        title="Users"
        subtitle="User, roles, permissions, password reset, lock, and organization assignment."
        right={<Button type="primary" onClick={() => openForm({ module: "user", mode: "create", source: "users", onSuccess: () => query.refetch() })}>Create User</Button>}
      />
      <PersistedTable tableId="users" rowKey="id" size="small" bordered loading={query.isLoading} dataSource={query.data ?? []} columns={columns} scroll={{ x: 980 }} />
      <RolesModal user={rolesUser} roles={roles.data?.map((x) => x.name) ?? ["Admin", "Accountant", "Staff"]} onClose={() => setRolesUser(null)} onSaved={() => query.refetch()} />
      <PermissionsModal user={permissionsUser} onClose={() => setPermissionsUser(null)} />
      <ResetPasswordModal user={passwordUser} onClose={() => setPasswordUser(null)} />
      <OrganizationAssignModal user={orgUser} onClose={() => setOrgUser(null)} />
    </div>
  );
}

function RolesModal({ user, roles, onClose, onSaved }: { user: UserRow | null; roles: string[]; onClose: () => void; onSaved: () => void }) {
  const { message } = App.useApp();
  const [value, setValue] = useState<string[]>([]);
  useEffect(() => setValue(user?.roles ?? []), [user]);

  return (
    <Modal title={`Roles - ${user?.email ?? ""}`} open={!!user} onCancel={onClose} onOk={async () => {
      if (!user) return;
      try {
        await updateUserRoles(user.id, value.length ? value : user.roles ?? []);
        message.success("Roles updated");
        onSaved();
        onClose();
      } catch (err) {
        message.error(getApiErrorMessage(err, "Unable to update roles"));
      }
    }}>
      <Select mode="multiple" style={{ width: "100%" }} value={value.length ? value : user?.roles ?? []} onChange={setValue} options={roles.map((x) => ({ value: x, label: x }))} />
    </Modal>
  );
}

function PermissionsModal({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const { message } = App.useApp();
  const query = useQuery({ queryKey: ["user-permissions", user?.id], enabled: !!user, queryFn: () => getUserPermissions(user!.id) });
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => setSelected(query.data ?? []), [query.data, user?.id]);
  const current = selected;
  const blocked = isFullAccessRole(user);

  const applyCascade = (group: string, changedPermission: string, checked: boolean) => {
    const action = changedPermission.split(".")[1];
    const index = permissionCascade.indexOf(action);
    if (index === -1) return;
    const groupPermissions = permissionGroups[group] ?? [];
    const affectedActions = checked
      ? permissionCascade.slice(0, index + 1)
      : permissionCascade.slice(index);
    const affectedPermissions = groupPermissions.filter((permission) => affectedActions.includes(permission.split(".")[1]));
    setSelected((prev) => {
      const next = new Set(prev);
      affectedPermissions.forEach((permission) => {
        if (checked) next.add(permission);
        else next.delete(permission);
      });
      return Array.from(next);
    });
  };

  return (
    <Modal title={`Permissions - ${user?.email ?? ""}`} open={!!user} onCancel={onClose} width={720} onOk={async () => {
      if (!user) return;
      if (blocked) {
        message.info("Admin / Accountant users already have full access.");
        onClose();
        return;
      }
      try {
        await assignUserPermissions(user.id, current);
        message.success("Permissions updated");
        onClose();
      } catch (err) {
        message.error(getApiErrorMessage(err, "Unable to update permissions"));
      }
    }} footer={(_, { OkBtn, CancelBtn }) => (
      <Space>
        <Button danger disabled={blocked} onClick={async () => {
          if (!user) return;
          try {
            await clearUserPermissions(user.id);
            message.success("Permissions cleared");
            onClose();
          } catch (err) {
            message.error(getApiErrorMessage(err, "Unable to clear permissions"));
          }
        }}>Clear</Button>
        <CancelBtn />
        <OkBtn />
      </Space>
    )}>
      <Space direction="vertical" style={{ width: "100%" }}>
        {blocked ? (
          <Alert
            showIcon
            type="info"
            message="Not applicable"
            description="Admin and Accountant users already have full access. Explicit permissions are only assigned to restricted users."
          />
        ) : (
          <Typography.Text type="secondary">Permissions cascade like the MVC screen: enabling Delete also enables Read/Create/Update; disabling Read clears higher actions.</Typography.Text>
        )}
        {Object.entries(permissionGroups).map(([group, permissions]) => (
          <div key={group}>
            <strong>{group}</strong>
            <div className="permissions-check-grid">
              {permissions.map((permission) => (
                <Checkbox
                  key={permission}
                  checked={current.includes(permission)}
                  disabled={blocked}
                  onChange={(event) => applyCascade(group, permission, event.target.checked)}
                >
                  {permission}
                </Checkbox>
              ))}
            </div>
          </div>
        ))}
      </Space>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  return (
    <Modal title={`Reset Password - ${user?.email ?? ""}`} open={!!user} onCancel={onClose} onOk={() => form.submit()}>
      <Form form={form} layout="vertical" onFinish={async (values) => {
        if (!user) return;
        try {
          await resetUserPassword(user.id, values.newPassword);
          message.success("Password reset");
          form.resetFields();
          onClose();
        } catch (err) {
          message.error(getApiErrorMessage(err, "Unable to reset password"));
        }
      }}>
        <Form.Item name="newPassword" label="New Password" rules={[{ required: true, min: 6 }]}><Input.Password /></Form.Item>
      </Form>
    </Modal>
  );
}

function OrganizationAssignModal({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const { message } = App.useApp();
  const [orgId, setOrgId] = useState<number | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const orgs = useQuery({ queryKey: ["organizations-for-user-modal"], enabled: !!user, queryFn: () => listResource<{ id: number; name: string; code?: string }>("/organizations/accessible") });
  const assigned = useQuery({ queryKey: ["user-orgs", user?.id], enabled: !!user, queryFn: () => getUserOrganizations(user!.id) });
  const orgNameById = new Map((orgs.data ?? []).map((org) => [org.id, org.name]));
  return (
    <Modal title={`Organizations - ${user?.email ?? ""}`} open={!!user} onCancel={onClose} onOk={async () => {
      if (!user || !orgId) return;
      try {
        await assignUserOrganization(orgId, user.id, isDefault);
        message.success("Organization assigned");
        assigned.refetch();
      } catch (err) {
        message.error(getApiErrorMessage(err, "Unable to assign organization"));
      }
    }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          placeholder="Select organization"
          style={{ width: "100%" }}
          value={orgId}
          onChange={setOrgId}
          options={(orgs.data ?? []).map((x) => ({ value: x.id, label: x.code ? `${x.name} (${x.code})` : x.name }))}
        />
        <Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)}>Default organization</Checkbox>
        <div>
          {(assigned.data ?? []).map((x) => <Tag key={x.organizationId}>{orgNameById.get(x.organizationId) ?? `Org ${x.organizationId}`}{x.isDefault ? " default" : ""}</Tag>)}
        </div>
      </Space>
    </Modal>
  );
}
